import type { ProductSummary } from '../../services/portfolio.service';
import { formatMoney } from '../../lib/datetime';
import {
  formatEarlyWithdrawal,
  formatRedemption,
  formatTerm,
} from '../../lib/redemptionTerms';
import { colors } from '../../styles/theme';
import { RISK_LEVELS } from '../../lib/riskLevels';

const TYPE_LABEL: Record<string, string> = {
  fixed_income: 'Fixed income',
  variable_income: 'Variable income',
  real_estate: 'Real Estate Series',
};

interface Props {
  readonly product: ProductSummary;
}

interface RowProps {
  readonly label: string;
  readonly value: string;
  readonly valueColor?: string;
}

function Row({ label, value, valueColor }: RowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        padding: '7px 0',
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <span style={{ fontSize: 12, color: colors.muted, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: valueColor ?? colors.ink, textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

export default function ProductDetailCard({ product }: Props): JSX.Element {
  const rate = product.target_yield
    ? `${(Number.parseFloat(product.target_yield) * 100).toFixed(2)}% p.a.`
    : '—';

  // Term, redemption and early withdrawal all render through the shared
  // derivation (lib/redemptionTerms — audit #138): the portfolio listing does
  // not carry the funds redemption fields, so this card falls back to "see
  // product details" rather than guessing.
  const lockIn = formatTerm(product);
  const redemption = formatRedemption(product) ?? 'See series details';
  const earlyWithdrawal = formatEarlyWithdrawal(product);

  const minInvest = formatMoney(product.min_investment, product.currency_code);

  const risk = product.risk_level == null ? null : RISK_LEVELS[product.risk_level];
  const typeLabel = product.asset_class ? (TYPE_LABEL[product.asset_class] ?? product.asset_class) : null;

  return (
    <div
      style={{
        borderRadius: 14,
        border: `1px solid ${colors.border}`,
        background: colors.surface,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.ink }}>
            {product.name}
          </h3>
          {typeLabel && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '2px 7px',
                borderRadius: 4,
                background: colors.border,
                color: colors.muted,
              }}
            >
              {typeLabel}
            </span>
          )}
          {risk && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 4,
                background: risk.bg,
                color: risk.color,
              }}
            >
              {risk.label}
            </span>
          )}
        </div>
        {product.description && (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: colors.inkSoft, lineHeight: 1.5 }}>
            {product.description}
          </p>
        )}
      </div>

      {/* Detail rows */}
      <div style={{ padding: '4px 18px 8px' }}>
        <Row label="Annual series result, for information" value={rate} valueColor={colors.gold} />
        <Row label="Term / lock-in" value={lockIn} />
        <Row label="Redemption" value={redemption} />
        <Row label="Minimum contribution" value={minInvest} />
        <Row label="Currency" value={product.currency_code ?? '—'} />
        <Row
          label="Early withdrawal"
          value={earlyWithdrawal.text}
          valueColor={earlyWithdrawal.tone === 'danger' ? colors.danger : undefined}
        />
      </div>

      {/* Regulatory disclaimer */}
      <div style={{ padding: '8px 18px 14px' }}>
        <p style={{ margin: 0, fontSize: 10, color: colors.muted, lineHeight: 1.5 }}>
          The percentage is for informational purposes, not a promised rate. What you receive is your
          share of what the series actually earns. Early withdrawal is subject to the terms
          of each series.
        </p>
      </div>
    </div>
  );
}
